// Pure helpers — no Supabase calls live here. Imports + writes happen in
// sendMessage / subscribeRealtime / createChannel / markRead.
import { localDate } from '../dateUtils.js'
import { firstName as _firstName } from '../peopleUtils.js'

export const rowToMessage = (r) => ({
  id: r.id,
  channelId: r.channel_id,
  authorEmail: r.author_email,
  body: r.body,
  attachments: r.attachments || null,
  replyTo: r.reply_to,
  threadRoot: r.thread_root,
  threadCount: r.thread_count || 0,
  editedAt: r.edited_at,
  deleted: !!r.deleted,
  createdAt: r.created_at
})

export const rowToChannel = (r) => ({
  id: r.id,
  kind: r.kind,
  name: r.name,
  topic: r.topic,
  isPrivate: !!r.is_private,
  isArchived: !!r.is_archived,
  createdBy: r.created_by,
  createdAt: r.created_at,
  updatedAt: r.updated_at
})

// Count unread messages for the current viewer in a single channel.
// `messages` is the channel's message array, `lastReadAt` is the viewer's
// last_read_at timestamp on chat_channel_members. Messages authored by the
// viewer don't count as unread.
export const unreadCount = (messages, lastReadAt, currentUserEmail) => {
  if (!Array.isArray(messages) || !messages.length) return 0
  const cutoff = lastReadAt ? new Date(lastReadAt).getTime() : 0
  let n = 0
  for (const m of messages) {
    if (m.deleted) continue
    if (m.authorEmail === currentUserEmail) continue
    const t = m.createdAt ? new Date(m.createdAt).getTime() : 0
    if (t > cutoff) n++
  }
  return n
}

// Per-(channelMembers identity, me) cache of the set of muted channel ids.
// Invalidates automatically when channelMembers is replaced (which happens
// on every realtime upsert). Drastically cuts the cost of `_isMuted` calls
// on every sidebar row during render.
const _mutedCache = new WeakMap()
export const mutedChannels = (channelMembers, me) => {
  if (!channelMembers || !me) return _EMPTY_SET
  let cached = _mutedCache.get(channelMembers)
  if (cached && cached._me === me) return cached
  const set = new Set()
  for (const m of channelMembers) {
    if (m.member_email === me && m.muted) set.add(m.channel_id)
  }
  set._me = me
  _mutedCache.set(channelMembers, set)
  return set
}
const _EMPTY_SET = new Set()

// DM channels have no name — synthesise one from the members. Pass
// `firstOnly: true` to drop surnames (used by the sidebar group-chat list
// to save horizontal space).
export const channelDisplayName = (channel, members, peopleByEmail, currentUserEmail, opts = {}) => {
  if (!channel) return ''
  if (channel.kind === 'channel') return channel.name || 'channel'
  const others = (members || [])
    .filter(m => m.channel_id === channel.id && m.member_email !== currentUserEmail)
    .map(m => {
      const full = peopleByEmail[m.member_email]?.name || m.member_email
      return opts.firstOnly ? _firstName(full) : full
    })
  if (channel.kind === 'dm') return others[0] || 'Direct message'
  return others.join(', ') || 'Group DM'
}

// Build the deduped list of dm / group_dm channels the current user belongs
// to. `external: false` keeps conversations where every other participant is
// internal (in `root.people`); `external: true` keeps conversations with at
// least one external participant. Both the sidebar DM list and the External
// People list use this with different `external` flags — factored out so
// they can't drift.
export const buildDMChannels = (root, external) => {
  if (!root) return []
  const me = root.currentUserEmail
  const members = root.channelMembers || []
  const peopleEmails = new Set((root.people || []).map((p) => p.email))

  const myChannelOthers = new Map()
  for (const ch of root.channels || []) {
    if (ch.kind !== 'dm' && ch.kind !== 'group_dm') continue
    const all = members
      .filter((m) => m.channel_id === ch.id)
      .map((m) => m.member_email)
    if (!all.includes(me)) continue
    const others = all.filter((e) => e !== me)
    if (!others.length) continue
    myChannelOthers.set(ch.id, others)
  }

  const seenIds = new Set()
  const seenMemberKeys = new Set()
  return (root.channels || []).filter((c) => {
    if (!myChannelOthers.has(c.id)) return false
    if (seenIds.has(c.id)) return false
    const others = myChannelOthers.get(c.id)
    const allInternal = others.every((e) => peopleEmails.has(e))
    if (external ? allInternal : !allInternal) return false
    const memberKey = others.slice().sort().join(',')
    if (seenMemberKeys.has(memberKey)) return false
    seenIds.add(c.id)
    seenMemberKeys.add(memberKey)
    return true
  })
}

// Group consecutive messages from the same author within a 5-minute window —
// the second and subsequent ones drop the avatar/name header (Slack style).
export const groupMessages = (messages = []) => {
  const FIVE_MIN = 5 * 60 * 1000
  let lastAuthor = null
  let lastTime = 0
  let lastDay = null
  return messages.map(m => {
    const t = new Date(m.createdAt).getTime()
    const day = localDate(new Date(m.createdAt))
    const newDay = day !== lastDay
    const grouped = !newDay && m.authorEmail === lastAuthor && (t - lastTime) < FIVE_MIN
    lastAuthor = m.authorEmail
    lastTime = t
    lastDay = day
    return { ...m, _grouped: grouped, _newDay: newDay, _day: day }
  })
}

